import {
  QAlert,
  QButton,
  QIcon,
  QLookup,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  useCurrentUser,
  useFeatureFlag,
} from '@qualio/ui-components';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { documentApi } from '../../../../../../api/document';
import { QualioDocument } from '../../../../../../api/model/document';
import { Group } from '../../../../../../api/model/group';
import { Tag } from '../../../../../../api/model/tag';
import { isCrossCompanyDocument } from '../../../../../../util/CurrentUser';

type TagsLookupProps = {
  isOpen: boolean;
  tags: Tag[];
  groups: Group[];
  qualioDocument: QualioDocument;
  onClose: () => void;
  onSuccess: () => void;
  onError: (e: unknown) => void;
};

const tagDataView: QLookup.DataView<Tag> = {
  name: {
    header: 'Name',
    width: 'auto',
    render: (_name, tag) => (
      <QStack direction="row" align="center" justify="flex-start" spacing={1}>
        <QText>{tag.name}</QText>
        {!!tag.group_ids.length && <QIcon iconName="Lock" size="xs" />}
      </QStack>
    ),
  },
};

export const TagsLookup = ({
  isOpen,
  tags,
  groups,
  qualioDocument,
  onClose,
  onSuccess,
  onError,
}: TagsLookupProps) => {
  const { t } = useTranslation();
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingTagIds, setPendingTagIds] = useState<number[]>([]);
  const [pendingPrivateTags, setPendingPrivateTags] = useState<Tag[]>([]);

  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);

  const filterOption = useCallback(
    (tag: Tag, searchTerm: string | undefined) => {
      if (!searchTerm) return true;
      return tag.name.toLowerCase().includes(searchTerm.toLowerCase());
    },
    [],
  );

  const filteredTags = useMemo(() => {
    const isShared = !!qualioDocument.shared_company_ids?.length;
    const isInherited = isCrossCompanyDocument(currentUser, qualioDocument);

    if (isShared || isInherited) {
      return tags.filter((tag) => tag.group_ids.length === 0);
    } else {
      return tags;
    }
  }, [qualioDocument, tags, currentUser]);

  const applyChanges = async (tagIds: number[]) => {
    setIsApplyingChanges(true);
    try {
      await documentApi.update({ tag_ids: tagIds }, qualioDocument.id);
      onSuccess();
      onClose();
      setIsWarningModalOpen(false);
    } catch (e: unknown) {
      onError(e);
    } finally {
      setIsApplyingChanges(false);
    }
  };

  const handleSelect = async (selectedTags: readonly Tag[]) => {
    const tagIds = selectedTags.map((tag) => tag.id);
    const privateTags = selectedTags.filter((tag) => tag.group_ids.length > 0);

    if (privateTags.length > 0) {
      setPendingTagIds(tagIds);
      setPendingPrivateTags(privateTags);
      setIsWarningModalOpen(true);
    } else {
      await applyChanges(tagIds);
    }
  };

  const getAffectedGroups = (): Group[] => {
    const affectedGroups = new Set<Group>();
    pendingPrivateTags.forEach((tag) => {
      tag.group_ids.forEach((groupId) => {
        const group = groups.find((g) => g.id === groupId);
        if (group) {
          affectedGroups.add(group);
        }
      });
    });
    return Array.from(affectedGroups);
  };

  const getGroupVisibilityText = () => {
    const affectedGroupNames = getAffectedGroups().map((group) => group.name);
    return (
      <>
        {t('labels.visibleToMembersOf')}{' '}
        {affectedGroupNames.map((groupName, idx) => {
          if (idx === 0) {
            return <b key={groupName}>{groupName}</b>;
          }
          if (idx > 0 && idx < affectedGroupNames.length - 1) {
            return (
              <>
                , <b key={groupName}>{groupName}</b>
              </>
            );
          } else {
            return (
              <>
                {' '}
                and <b key={groupName}>{groupName}</b>
              </>
            );
          }
        })}{' '}
        {t(affectedGroupNames.length > 1 ? 'labels.groups' : 'labels.group')}{' '}
        only.
        {enterpriseAccounts && (
          <>
            <br />
            <br />
            {t('messages.privateTagsPreventSharingToChildren')}
          </>
        )}
        {qualioDocument.training_available && (
          <>
            <br />
            <br />
            {t('labels.traineesWhoDoNotBelongTo')}{' '}
            {affectedGroupNames.length > 1
              ? t('labels.atLeastOneOfThoseGroups')
              : t('labels.thisGroup')}{' '}
            {t('labels.willNotHaveAccessForTraining')}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <QLookup.DataProvider.Fixed<Tag>
        data={filteredTags}
        filterOption={filterOption}
      >
        <QLookup.MultiSelect<Tag>
          isOpen={isOpen}
          action={t('buttons.applyChanges')}
          searchPlaceholder={t('forms.filterByTags')}
          accessors={{ id: 'id' }}
          isItemPreSelected={(tag) => qualioDocument.tag_ids.includes(tag.id)}
          onSelect={handleSelect}
          onCancel={onClose}
          view={tagDataView}
        >
          <QModalHeader>
            <QText>{t('buttons.manageTags')}</QText>
          </QModalHeader>
        </QLookup.MultiSelect>
      </QLookup.DataProvider.Fixed>
      {isWarningModalOpen && (
        <QModal isOpen onClose={() => setIsWarningModalOpen(false)}>
          <QModalHeader>
            <QText>{t('buttons.manageTags')}</QText>
          </QModalHeader>
          <QModalBody>
            <QAlert
              status="warning"
              title={t('messages.thisActionAffectsContentVisibility')}
              description={t('messages.someUsersAbilityToViewAffected')}
            />
            <QText fontSize="16px" pt="4">
              {getGroupVisibilityText()}
            </QText>
          </QModalBody>
          <QModalActions>
            <QButton
              variant="outline"
              onClick={() => setIsWarningModalOpen(false)}
            >
              {t('buttons.cancel')}
            </QButton>
            <QButton
              isLoading={isApplyingChanges}
              onClick={() => void applyChanges(pendingTagIds)}
            >
              {t('buttons.continue')}
            </QButton>
          </QModalActions>
        </QModal>
      )}
    </>
  );
};
